import {
  Color,
  NgxMatColorPickerModule,
} from '@angular-material-components/color-picker';
import { Component, DestroyRef, Inject, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogClose,
} from '@angular/material/dialog';
import { ToastAlertService } from '@core/services';
import { Theme } from '../../../models/theme.model';
import { ThemeService } from '../../../services/theme.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TokenService } from '../../../../public/services';
import { ButtonComponent } from '@shared/components/button/button.component';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TitleComponent } from '@shared/components';

@Component({
  selector: 'app-themes-modal',
  templateUrl: './themes-modal.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    NgxMatColorPickerModule,
    ButtonComponent,
    MatDialogClose,
    TitleComponent,
  ],
})
export class ThemesModalComponent {
  public color: ThemePalette = 'primary';
  public touchUi = false;
  public themeForm!: FormGroup;

  private formBuilder = inject(FormBuilder);
  private alertService = inject(ToastAlertService);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<ThemesModalComponent>);
  private tokenService = inject(TokenService);

  constructor(@Inject(MAT_DIALOG_DATA) public data: Theme) {
    this.buildForm();
  }

  public onSubmit(): void {
    if (this.themeForm.valid) {
      const myTheme: Theme = {
        idTheme: this.data?.idTheme,
        name: this.themeForm.value['name'],
        title: '#' + this.themeForm?.value['title'].hex,
        primary: '#' + this.themeForm?.value['primary'].hex,
        accent: '#' + this.themeForm?.value['accent'].hex,
        warn: '#' + this.themeForm?.value['warn'].hex,
        general: '#' + this.themeForm?.value['general'].hex,
        itemNavBar: '#' + this.themeForm?.value['itemNavBar'].hex,
      };

      if (this.data) {
        console.log(this.data);
        this.themeService
          .updateTheme(myTheme)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: resp => {
              this.dialogRef.close(true);
              if (resp.code === 10000) {
                this.alertService.sucess('Tema modificado exitosamente');
                this.themeService.setTheme(this.tokenService.getTheme());
              } else {
                this.alertService.error(resp.detail);
              }
            },
            error: error => {
              this.alertService.error(`Error: ${error}`);
            },
          });
      } else {
        this.themeService
          .storeTheme(myTheme)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: resp => {
              this.dialogRef.close(true);
              if (resp.code === 10000) {
                this.alertService.sucess('Tema creado exitosamente');
              } else {
                this.alertService.error(resp.detail);
              }
            },
            error: error => {
              this.alertService.error(`Error: ${error}`);
            },
          });
      }
    }
  }

  public hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  private buildForm(): void {
    if (this.data) {
      this.themeForm = this.formBuilder.group({
        idTheme: [this.data?.idTheme],
        name: [this.data.name, Validators.required],
        title: [
          new Color(
            this.hexToRgb(this.data.title)?.r as number,
            this.hexToRgb(this.data.title)?.g as number,
            this.hexToRgb(this.data.title)?.b as number
          ),
          Validators.required,
        ],
        primary: [
          new Color(
            this.hexToRgb(this.data.primary)?.r as number,
            this.hexToRgb(this.data.primary)?.g as number,
            this.hexToRgb(this.data.primary)?.b as number
          ),
          Validators.required,
        ],
        accent: [
          new Color(
            this.hexToRgb(this.data.accent)?.r as number,
            this.hexToRgb(this.data.accent)?.g as number,
            this.hexToRgb(this.data.accent)?.b as number
          ),
          Validators.required,
        ],
        warn: [
          new Color(
            this.hexToRgb(this.data.warn)?.r as number,
            this.hexToRgb(this.data.warn)?.g as number,
            this.hexToRgb(this.data.warn)?.b as number
          ),
          Validators.required,
        ],
        general: [
          new Color(
            this.hexToRgb(this.data.general)?.r as number,
            this.hexToRgb(this.data.general)?.g as number,
            this.hexToRgb(this.data.general)?.b as number
          ),
          Validators.required,
        ],
        itemNavBar: [
          new Color(
            this.hexToRgb(this.data.itemNavBar)?.r as number,
            this.hexToRgb(this.data.itemNavBar)?.g as number,
            this.hexToRgb(this.data.itemNavBar)?.b as number
          ),
          Validators.required,
        ],
      });
    } else {
      this.themeForm = this.formBuilder.group({
        idTheme: [0],
        name: ['', Validators.required],
        title: ['', [Validators.required, this.validateHexColor]],
        primary: ['', [Validators.required, this.validateHexColor]],
        accent: ['', [Validators.required, this.validateHexColor]],
        warn: ['', [Validators.required, this.validateHexColor]],
        general: ['', [Validators.required, this.validateHexColor]],
        itemNavBar: ['', [Validators.required, this.validateHexColor]],
      });
    }
  }
  validateHexColor(control: any) {
    const hexColorRegex = /^#([0-9a-fA-F]{3}){1,2}$/;
    if (!hexColorRegex.test(control.value)) {
      return { invalidHexColor: true };
    } else {
      return null;
    }
  }
}
